import { User, Mail, Phone, MapPin, GraduationCap, Briefcase, Award, FileText, Target, Image as ImageIcon } from 'lucide-react';

interface ResumeSnapshot {
  user_id?: string;
  user_name?: string | null;
  user_email?: string | null;
  user_phone?: string | null;
  photo_url?: string | null;
  education?: any[];
  experience?: any[];
  awards?: any[];
  certificates_list?: any[];
  introduction?: string | null;
  portfolio_images?: string[];
  desired_position?: string | null;
  desired_salary_type?: string | null;
  desired_salary_min?: number | null;
  desired_salary_max?: number | null;
  desired_location?: string | null;
  desired_work_type?: string | null;
  address?: string | null;
  snapshot_at?: string;
}

interface Props {
  snapshot: ResumeSnapshot | null | undefined;
}

const SALARY_TYPE_LABELS: Record<string, string> = {
  HOURLY: '시급',
  DAILY: '일급',
  MONTHLY: '월급',
  YEARLY: '연봉',
  NEGOTIABLE: '협의',
};

const WORK_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: '정규직',
  CONTRACT: '계약직',
  PART_TIME: '파트타임',
  FREELANCE: '프리랜서',
};

export default function ApplicationDetailContent({ snapshot }: Props) {
  if (!snapshot) {
    return <div className="text-sm text-gray-500">이력서 정보가 없습니다.</div>;
  }

  const formatSalary = () => {
    if (!snapshot.desired_salary_type) return null;
    const label = SALARY_TYPE_LABELS[snapshot.desired_salary_type] || snapshot.desired_salary_type;
    if (snapshot.desired_salary_type === 'NEGOTIABLE') return label;
    const min = snapshot.desired_salary_min || 0;
    const max = snapshot.desired_salary_max || 0;
    if (!min && !max) return label;
    return `${label} ${min.toLocaleString()}~${max.toLocaleString()}원`;
  };

  return (
    <div className="space-y-5">
      {/* 프로필 헤더 */}
      <div className="flex items-start gap-4">
        {snapshot.photo_url ? (
          <img src={snapshot.photo_url} alt="" className="w-20 h-20 rounded-xl object-cover border border-gray-200" />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center">
            <User className="w-10 h-10 text-primary" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900">{snapshot.user_name || '지원자'}</h3>
          <div className="mt-1 space-y-0.5 text-sm text-gray-600">
            {snapshot.user_email && (
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-gray-400" />
                <span>{snapshot.user_email}</span>
              </div>
            )}
            {snapshot.user_phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                <span>{snapshot.user_phone}</span>
              </div>
            )}
            {snapshot.address && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                <span>{snapshot.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 희망 조건 */}
      {(snapshot.desired_position || snapshot.desired_location || formatSalary() || snapshot.desired_work_type) && (
        <section className="bg-primary/5 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <Target className="w-4 h-4 text-primary" />
            희망 조건
          </h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {snapshot.desired_position && (
              <div>
                <span className="text-gray-500 text-xs block">희망 포지션</span>
                <span className="text-gray-900 font-medium">{snapshot.desired_position}</span>
              </div>
            )}
            {snapshot.desired_work_type && (
              <div>
                <span className="text-gray-500 text-xs block">근무 형태</span>
                <span className="text-gray-900 font-medium">
                  {WORK_TYPE_LABELS[snapshot.desired_work_type] || snapshot.desired_work_type}
                </span>
              </div>
            )}
            {formatSalary() && (
              <div>
                <span className="text-gray-500 text-xs block">희망 급여</span>
                <span className="text-gray-900 font-medium">{formatSalary()}</span>
              </div>
            )}
            {snapshot.desired_location && (
              <div>
                <span className="text-gray-500 text-xs block">희망 지역</span>
                <span className="text-gray-900 font-medium">{snapshot.desired_location}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 자기소개 */}
      {snapshot.introduction && (
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-primary" />
            자기소개
          </h4>
          <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: snapshot.introduction }} />
        </section>
      )}

      {/* 포트폴리오 */}
      {Array.isArray(snapshot.portfolio_images) && snapshot.portfolio_images.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <ImageIcon className="w-4 h-4 text-primary" />
            포트폴리오
          </h4>
          <div className="flex flex-col gap-3">
            {snapshot.portfolio_images.map((url, idx) => (
              <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block">
                <img src={url} alt={`포트폴리오 ${idx + 1}`} className="block max-w-full h-auto rounded-lg border border-gray-200 hover:opacity-90 transition-opacity" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* 학력 */}
      {Array.isArray(snapshot.education) && snapshot.education.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <GraduationCap className="w-4 h-4 text-primary" />
            학력
          </h4>
          <ul className="space-y-2">
            {snapshot.education.map((edu: any, idx: number) => (
              <li key={idx} className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <div className="font-medium text-gray-900">{edu.school || edu.name || '-'}</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {[edu.major, edu.degree].filter(Boolean).join(' · ')}
                </div>
                {(edu.start_date || edu.end_date) && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {edu.start_date} ~ {edu.end_date || '재학중'}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 경력 */}
      {Array.isArray(snapshot.experience) && snapshot.experience.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <Briefcase className="w-4 h-4 text-primary" />
            경력
          </h4>
          <ul className="space-y-2">
            {snapshot.experience.map((exp: any, idx: number) => (
              <li key={idx} className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <div className="font-medium text-gray-900">{exp.company || exp.name || '-'}</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {[exp.position, exp.role].filter(Boolean).join(' · ')}
                </div>
                {(exp.start_date || exp.end_date) && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {exp.start_date} ~ {exp.end_date || '재직중'}
                  </div>
                )}
                {exp.description && (
                  <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{exp.description}</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 자격증 */}
      {Array.isArray(snapshot.certificates_list) && snapshot.certificates_list.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <Award className="w-4 h-4 text-primary" />
            자격증
          </h4>
          <ul className="space-y-1.5">
            {snapshot.certificates_list.map((cert: any, idx: number) => (
              <li key={idx} className="text-sm text-gray-700">
                <span className="font-medium">{cert.name || '-'}</span>
                {cert.issuer && <span className="text-gray-500 ml-1">· {cert.issuer}</span>}
                {cert.acquired_date && <span className="text-gray-500 ml-1">· {cert.acquired_date}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 수상 */}
      {Array.isArray(snapshot.awards) && snapshot.awards.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <Award className="w-4 h-4 text-primary" />
            수상 경력
          </h4>
          <ul className="space-y-1.5">
            {snapshot.awards.map((award: any, idx: number) => (
              <li key={idx} className="text-sm text-gray-700">
                <span className="font-medium">{award.title || award.name || '-'}</span>
                {award.organization && <span className="text-gray-500 ml-1">· {award.organization}</span>}
                {award.date && <span className="text-gray-500 ml-1">· {award.date}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {snapshot.snapshot_at && (
        <div className="text-xs text-gray-400 border-t pt-2">
          지원 시점 이력서 · {new Date(snapshot.snapshot_at).toLocaleString('ko-KR')}
        </div>
      )}
    </div>
  );
}
